import { Dropdown, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useState } from "react"
import styled, { useTheme } from "styled-components"

import AppTable from "../../../_shared/components/AppTable"
import { useStateValue } from "../../../_shared/context/AppStateStore"
import usePaginatedRestResource from "../../../_shared/hooks/usePaginatedRestResource"
import {
    acceptTenancyAsIs,
    acceptTenancyWithModifications,
    rejectTenancy,
} from "../../../api"
import TenanciesBottomSheet from "./TenanciesBottomSheet"
import TenanciesFilter from "./TenanciesFilter"

// ----------------------------------------------------------------------------

function _RoomTenancies(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------
    const [{ token }] = useStateValue()
    const [tenancies, fetch, , pagination, , handleFiltersChange] =
        usePaginatedRestResource("/room-tenancies", token, {
            filters: { status: "submitted" },
        })

    const [selectedItem, setSelectedItem] = useState()

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleFiltersReceived(filters) {
        handleFiltersChange(filters)
    }

    async function handleEdit(values) {
        try {
            const id = values.id
            await acceptTenancyWithModifications(id, values, token)
            fetch()
            message.success("Operazione eseguita con successo")
            setSelectedItem(null)
        } catch (e) {
            console.log(e)
            message.success("Operazione eseguita con successo")
        }
    }

    async function handleAccept(row) {
        try {
            await acceptTenancyAsIs(row.id, token)
            fetch()
            message.success("Operazione eseguita con successo")
            setSelectedItem(null)
        } catch (e) {
            console.log(e)
            message.success("Operazione eseguita con successo")
        }
    }

    async function handleReject(row) {
        try {
            await rejectTenancy(row.id, token)
            fetch()
            message.success("Operazione eseguita con successo")
            setSelectedItem(null)
        } catch (e) {
            console.log(e)
            message.success("Operazione eseguita con successo")
        }
    }

    function renderActions(row) {
        return (
            <Actions
                row={row}
                handleAccept={handleAccept}
                handleReject={handleReject}
                handleEdit={handleEdit}
                setSelectedItem={setSelectedItem}
            />
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <TenanciesFilter
                className="filter"
                onFilter={handleFiltersReceived}
            />
            <AppTable
                title="Inquilini"
                headers={getColumns(renderActions)}
                rows={tenancies}
                className="table"
                pagination={{
                    ...pagination,
                    hideOnSinglePage: false,
                    showTotal: false,
                    position: ["bottomRight", "none"],
                }}
                onChange={(pagination) => fetch({ pagination })}
            />
            <TenanciesBottomSheet
                open={!!selectedItem}
                defaultValues={selectedItem}
                onConfirm={handleEdit}
                onCancel={() => setSelectedItem(null)}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_RoomTenancies.propTypes = {
    className: PropTypes.string.isRequired,
}

_RoomTenancies.defaultProps = {}

// ----------------------------------------------------------------------------

const RoomTenancies = styled(_RoomTenancies)`
    & {
        padding: 40px 60px;

        .table {
            overflow-x: auto;
            min-height: 600px;
        }

        .actions {
            .actions-selector {
                height: 35px;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0px 15px;
                border-radius: 4px;
                border: 1px solid black;
                cursor: pointer;
                box-sizing: border-box;
            }
            .actions-dropdown {
                background-color: white;
                width: 200px;
                box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.1);

                .actions-dropdown-action {
                    padding: 10px 15px;
                    cursor: pointer;
                    transition: all 200ms ease;

                    &:hover {
                        background-color: ${({ theme }) =>
                            theme.colors.trasparentGrayBg};
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default RoomTenancies

export function getColumns(renderActions) {
    return [
        {
            title: "Agenzia",
            key: "agency",
            render: (r) => r?.createdBy?.businessName,
        },
        {
            title: "Codice stanza",
            key: "room",
            dataIndex: "room",
            // eslint-disable-next-line
            render: (room) => (
                <a target="__blank" href={getRoomUrl(room)}>
                    {room}
                </a>
            ),
        },
        {
            title: "Nome e cognome",
            key: "name",
            render: (r) => r.tenant?.fullName,
        },
        {
            title: "E-mail",
            key: "email",
            render: (r) => r?.tenant?.email,
        },
        {
            title: "Luogo di nascita",
            key: "birthPlace",
            render: (r) => r?.tenant?.birthPlace,
        },
        {
            title: "Data di nascita",
            key: "birthDate",
            render: (r) => dayjs(r?.tenant?.birthDate).format("DD/MM/YYYY"),
        },
        {
            title: "Codice fiscale",
            key: "identityCardNumber",
            render: (r) => r?.tenant?.fiscalCode,
        },
        {
            title: "Stato",
            dataIndex: "status",
            key: "status",
            // eslint-disable-next-line
            render: (status) => <StatusDot status={status} />,
        },
        { key: "actions", render: renderActions },
    ]
}

function Actions({
    // eslint-disable-next-line
    handleAccept,
    // eslint-disable-next-line
    handleReject,
    // eslint-disable-next-line
    row,
    // eslint-disable-next-line
    setSelectedItem,
}) {
    const [visible, setVisible] = useState()
    function withClose(callback) {
        return (e) => {
            e.stopPropagation()
            callback()
            setVisible(false)
        }
    }
    return (
        <div className="actions">
            <Dropdown
                trigger="click"
                getPopupContainer={(node) => node.parentElement}
                visible={visible}
                onVisibleChange={(newState) => setVisible(newState)}
                overlay={
                    <div
                        className="actions-dropdown"
                        onClick={withClose(() => handleAccept(row))}
                    >
                        <div className="actions-dropdown-action">Accetta</div>
                        <div
                            className="actions-dropdown-action"
                            onClick={withClose(() => setSelectedItem(row))}
                        >
                            Accetta con modifiche
                        </div>
                        <div
                            className="actions-dropdown-action"
                            onClick={withClose(() => handleReject(row))}
                        >
                            Rifiuta
                        </div>
                    </div>
                }
            >
                <div className="actions-selector">Azioni</div>
            </Dropdown>
        </div>
    )
}

function StatusDot({ status }) {
    let color
    const theme = useTheme()
    if (status === "submitted") color = theme.colors.pending
    if (status === "accepted") color = theme.colors.success
    if (status === "rejected") color = theme.colors.error
    return (
        <div
            style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: color,
            }}
        />
    )
}

function getRoomUrl(roomId) {
    if (process.env.NODE_ENV === "development") {
        return `http://localhost:3000/alloggi/${roomId}`
    } else {
        return `${window.location.protocol}//${window.location.hostname}/alloggi/${roomId}`
    }
}
